// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { ServiceResult } from "@insite/client-framework/Services/ApiService";
import { getRetailUsers, RetailUsersApiParameter, UsersModel } from "@synd/App/Services/RetailerManagementServices";

type HandlerType = ApiHandlerDiscreteParameter<
    HasOnSuccess<UsersModel> & HasOnError<string> & RetailUsersApiParameter,
    RetailUsersApiParameter,
    ServiceResult<UsersModel>
>;

export const DispatchBeginData: HandlerType = props => {
    props.dispatch({
        type: "RetailerManagement/BeginLoadData",
        parameter: props.parameter,
    });
};

export const PopulateApiParameter: HandlerType = props => {
    const { onSuccess, onError, ...apiParameter } = props.parameter;
    props.apiParameter = apiParameter;
};

export const RequestDataFromApi: HandlerType = async props => {
    props.apiResult = await getRetailUsers(props.apiParameter);
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult.successful) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.dispatch({
            type: "RetailerManagement/FailedToLoadData",
        });
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.successful) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.dispatch({
            type: "RetailerManagement/CompleteLoadData",
            collection: props.apiResult.result,
            parameter: props.parameter,
        });
    }
};

export const chain = [
    DispatchBeginData,
    PopulateApiParameter,
    RequestDataFromApi,
    ExecuteOnErrorCallback,
    ExecuteOnSuccessCallback,
];

const loadRetailerManagementUsers = createHandlerChainRunner(chain, "loadRetailerManagementUsers");
export default loadRetailerManagementUsers;
